import React, { FC, useEffect } from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';
import Svg from 'svg-inline-react';
import { FUNNEL_MAIN, FUNNEL_OVERVIEW_ROUTE } from '@src/Routes/links';
import {
  useCompanyQuery,
  useOpeningOverviewQueryLazyQuery,
  useRelocationBenefitsQuery,
  OpeningSourceEnum,
  OpeningStateEnum,
  useOpeningQueryLazyQuery,
} from '@src/graphql/generated';
import { Type, Wrapper } from '@src/components/Wrapper';
import { Container } from '@src/components/Container';
import { Sidebar } from '@src/components/Sidebar';
import { SkillsReview } from '@src/components/SkillsReview';
import { SalaryReview } from '@src/components/SalaryReview';
import Collaborators from '@src/containers/Collaborators';
import { OpeningActions } from '@src/components/OpeningActions';
import { ActionMenuPopover } from '@src/components/Popover';
import { MetricContext } from '@src/metrics/metrics.enum';
import { isAdminReview, isInactive, isActive, isPaused } from '@src/util/opening';
import { OPENING_ROUTE } from '@src/components/Preview/appRoutes';
import { CandidateDestinationInfo } from '@src/components/CandidateDestinationInfo';
import FrHeartSvg from '@src/svgs/fr-heart.svg';
import { ActiveTab } from '@src/components/Sidebar/enums/activeTab.enum';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { OpeningCandidateProfile } from '@src/containers/OpeningCandidateProfile';
import { createLink } from '@src/util/createLink';
import { isReviewEnabled } from '../CreateFunnel/util/creationSteps';
import { PremiumServices } from './PremiumServices';
import { ShareOpening } from './ShareOpening';
import OpeningActionsButtons from './OpeningActionsButtons';
import './styles.scss';

export const FunnelOverview: FC = () => {
  const match = useRouteMatch<{ openingId: string }>(FUNNEL_OVERVIEW_ROUTE);

  const openingId = match?.params?.openingId;

  const { data: benefitsData, loading: isBenefitsLoading } = useRelocationBenefitsQuery();

  const benefits = benefitsData?.relocationBenefits;

  const { data: companyData, loading: isCompanyLoading } = useCompanyQuery();

  const companyProfile = companyData?.company.profile;

  const [fetchOpening, { data: openingData, loading: isOpeningLoading, updateQuery: updateOpeningQuery }] =
    useOpeningOverviewQueryLazyQuery();

  const opening = openingData?.opening;

  const [getFunnelOpening, { data: funnelOpeningData, loading: isFunnelOpeningLoading }] = useOpeningQueryLazyQuery();

  const funnelOpening = funnelOpeningData?.opening;

  const updateOpeningState = (state: OpeningStateEnum): void => {
    updateOpeningQuery(prev => {
      return {
        ...prev,
        opening: {
          ...prev.opening,
          state: state,
        },
      };
    });
  };

  const recruiter = opening?.recruiter;
  const recruiterRights = opening?.recruiter?.rights;
  const isSidebarShown = companyProfile && companyProfile.isComplete;

  const headerButtonActions = isInactive(opening?.state) ? [] : ['activate', 'pause', 'stop', 'republish'];

  const headerDropdownActions = isInactive(opening?.state)
    ? ['activate', 'duplicate', 'export', 'archive']
    : ['duplicate', 'export', 'archive'];

  const isOpeningGroupVisible = isActive(opening?.state) || isPaused(opening?.state) || isInactive(opening?.state);

  const isLoading = isBenefitsLoading || isCompanyLoading || isOpeningLoading || isFunnelOpeningLoading;

  useEffect(() => {
    if (openingId) {
      fetchOpening({
        variables: { id: openingId },
      });

      getFunnelOpening({
        variables: { openingId: openingId },
      });
    }
  }, [openingId]);

  if (isLoading) {
    return <div className="funnel-overview loading">Loading...</div>;
  }

  if (funnelOpening && !isReviewEnabled(funnelOpening)) {
    return (
      <Redirect
        to={createLink(FUNNEL_MAIN, {
          params: {
            openingId: openingId,
          },
        })}
      />
    );
  }

  if (!opening) {
    return <div className="funnel-overview">Opening not found</div>;
  }

  return (
    <>
      <div className={classnames('funnel-overview', isAdminReview(opening?.state) && 'review')}>
        <Helmet>
          <title>Opening overview</title>
        </Helmet>

        <header
          className={classnames('funnel-overview-header', !isOpeningGroupVisible && 'funnel-overview-header--divider')}
        >
          <Wrapper className="funnel-overview-header-wrapper" type={Type.HORIZONTAL}>
            <>
              <h2>{opening?.title}</h2>

              <div className="funnel-overview-header-actions-wrapper">
                {opening && (
                  <OpeningActions
                    actionSelection={headerButtonActions}
                    component={OpeningActionsButtons}
                    metricsPrevContext={MetricContext.OPENING_VIEW}
                    opening={opening}
                    updateOpening={opening => {
                      if (!opening.state) {
                        return;
                      }

                      updateOpeningState(opening.state);
                    }}
                  >
                    {({ actions, handleAction }) => (
                      <OpeningActionsButtons actions={actions} onAction={handleAction} opening={opening} />
                    )}
                  </OpeningActions>
                )}

                {opening?.source === OpeningSourceEnum.MEETFRANK && (
                  <span className="funnel-overview-header-actions-wrapper-menu">
                    <OpeningActions
                      actionSelection={headerDropdownActions}
                      metricsPrevContext={MetricContext.OPENING_VIEW}
                      opening={opening}
                      updateOpening={opening => {
                        if (!opening.state) {
                          return;
                        }

                        updateOpeningState(opening.state);
                      }}
                    >
                      {({ actions, handleAction }) => <ActionMenuPopover actions={actions} onAction={handleAction} />}
                    </OpeningActions>
                  </span>
                )}
              </div>
            </>
          </Wrapper>
        </header>

        <Wrapper className="funnel-overview-opening" type={Type.HORIZONTAL}>
          <>
            {!isOpeningGroupVisible && (
              <div className="funnel-overview-opening__in-review-empty-state-wrapper">
                <h3>Direct Applicants</h3>

                <div className="funnel-overview-opening__in-review-empty-state">
                  <Svg src={FrHeartSvg} />

                  <p>
                    You will start receiving direct applicants <br /> once your offer is reviewed.{' '}
                  </p>
                </div>
              </div>
            )}

            <div className="funnel-overview-opening-content">
              <Container className="page-content" type={ContainerType.BORDERED}>
                {isAdminReview(opening?.state) && (
                  <div className="funnel-overview-review-notice">
                    In review. Our moderator will review it in 72 hours.
                  </div>
                )}

                {opening?.candidateDestination && (
                  <CandidateDestinationInfo candidateDestination={opening?.candidateDestination} />
                )}

                {opening && <SkillsReview opening={opening} />}

                {opening?.source === OpeningSourceEnum.MEETFRANK && <SalaryReview opening={opening} />}

                {recruiterRights?.inviteCollaborator && !recruiter?.isSiteAdmin && (
                  <Collaborators
                    buttonLabel="Manage collaborators"
                    metricsContext={MetricContext.OPENING_VIEW}
                    opening={opening}
                    title="Collaborators"
                  />
                )}

                {!!opening?.paidAddons.length && <PremiumServices openingPaidAddons={opening.paidAddons} />}

                {!!opening.publicUrl && <ShareOpening openingId={opening.id} openingUrl={opening.publicUrl} />}
              </Container>

              {isSidebarShown && (
                <Sidebar
                  benefits={benefits}
                  companyProfile={companyProfile}
                  defaultTab={ActiveTab.PREVIEW}
                  isTabsDisabled
                  opening={opening}
                  previewUrl={OPENING_ROUTE}
                />
              )}
            </div>
          </>
        </Wrapper>
      </div>

      <OpeningCandidateProfile />
    </>
  );
};
